import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Alert, CircularProgress, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel,
} from '@mui/material';

import { failedLoginsAPI, usersAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';

const REVIEW_LABELS = { open: 'Not started', in_review: 'In review', review_complete: 'Review complete', approved: 'Approved' };
const DEADLINE_CHIP = { approved: 'success', on_track: 'default', due_soon: 'warning', overdue: 'error' };
const NATIVE = { select: { native: true }, inputLabel: { shrink: true } };

const fmt = (d) => (d ? new Date(d).toLocaleString() : '-');
const fmtLocal = (d) => (d ? String(d).replace('T', ' ').slice(0, 19) : '-');

function fmtDue(deadline) {
  if (!deadline) return '';
  const tz = deadline.timezone;
  const opts = { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false };
  try { return new Date(deadline.due_at).toLocaleString('en-GB', tz ? { ...opts, timeZone: tz } : opts) + (tz ? ` ${tz}` : ''); }
  catch (e) { return new Date(deadline.due_at).toLocaleString(); }
}

function download(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

// An exception is a login that failed on an instance (or an instance that did not report)
export const exceptionKey = (e) => `${e.instance_id}|${e.kind || 'login'}|${e.login_name ?? ''}`;

function FailedLogins() {
  const { canReviewBackups, email } = usePermissions();
  const canReview = canReviewBackups();

  const [weeks, setWeeks] = useState([]);
  const [week, setWeek] = useState('');
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ instance_id: '', search: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [text, setText] = useState('');
  const [events, setEvents] = useState(null);
  const [actionForm, setActionForm] = useState({ kind: 'fix', description: '', assigned_to: '', due_date: '', whole_instance: false });

  const fail = (err, fallback) => setError(err.response?.status === 401
    ? 'Your sign-in has expired. You are being taken to sign in again; if nothing happens, sign out and back in.'
    : (err.response?.data?.detail || fallback));

  const loadWeeks = useCallback(async () => {
    try {
      const res = await failedLoginsAPI.weeks();
      setWeeks(res.data);
      setWeek((current) => current || (res.data[0] ? String(res.data[0].week_start).slice(0, 10) : ''));
    } catch (err) { fail(err, 'Failed to load weeks'); } finally { setLoading(false); }
  }, []);

  const loadWeek = useCallback(async () => {
    if (!week) { setData(null); return; }
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      setData((await failedLoginsAPI.week(week, params)).data);
    } catch (err) { fail(err, 'Failed to load the week'); }
  }, [week, filters]);

  useEffect(() => { loadWeeks(); usersAPI.list().then((r) => setUsers(r.data)).catch(() => {}); }, [loadWeeks]);
  useEffect(() => { loadWeek(); }, [loadWeek]);

  const run = async (fn, okMessage) => {
    setError(null); setSuccess(null);
    try {
      const res = await fn();
      setSuccess(typeof okMessage === 'function' ? okMessage(res) : okMessage);
      setDialog(null); setText('');
      await Promise.all([loadWeek(), loadWeeks()]);
      return res;
    } catch (err) { fail(err, 'That did not work'); return null; }
  };

  const exportWeek = async (format) => {
    try {
      const params = { format };
      if (filters.instance_id) params.instance_id = filters.instance_id;
      download((await failedLoginsAPI.exportWeek(week, params)).data, `FailedLogins_${week}.${format}`);
    } catch (err) { fail(err, 'Export failed'); }
  };

  const showEvents = async (g) => {
    setEvents({ group: g, rows: null });
    try {
      const res = await failedLoginsAPI.events(week, { instance_id: g.instance_id, login_name: g.login_name === null ? '__none__' : g.login_name });
      setEvents({ group: g, rows: res.data });
    } catch (err) { setEvents(null); fail(err, 'Could not load the events'); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (!weeks.length) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Failed Logins</Typography>
        <Alert severity="info">No failed login reports have been received yet. Each SQL Server instance sends one every week from its Agent job.</Alert>
      </Box>
    );
  }

  const review = data?.review || { status: 'open' };
  const status = review.status;
  const uncovered = data?.uncovered || [];
  const uncoveredKeys = new Set(uncovered.map(exceptionKey));
  const editable = canReview && (status === 'in_review' || status === 'review_complete');
  const isReviewer = (e) => !!review.reviewer_email && e.toLowerCase() === review.reviewer_email.toLowerCase();
  const me = (email || '').toLowerCase();
  const myOpenActions = (data?.actions || []).filter((a) => a.status === 'open' && a.assigned_to.toLowerCase() === me);

  const rows = data ? [
    ...data.groups.map((g) => ({ ...g, kind: 'login' })),
    ...data.exceptions.filter((e) => e.kind === 'missing'),
  ] : [];

  const openActionDialog = (row) => {
    setActionForm({ kind: 'fix', description: '', assigned_to: '', due_date: '', whole_instance: row.kind === 'missing' });
    setDialog({ type: 'action', row });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Failed Logins</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Weekly review of failed SQL Server logins, per instance. Every login that failed needs an action: fix it, or accept the risk with a reason.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" label="Week starting" value={week} sx={{ minWidth: 280 }} onChange={(e) => setWeek(e.target.value)}>
          {weeks.map((w) => {
            const d = String(w.week_start).slice(0, 10);
            return (
              <MenuItem key={d} value={d}>
                {d} — {REVIEW_LABELS[w.status]}{w.deadline && w.deadline.state === 'overdue' ? ' · OVERDUE' : ''} · {w.events} failed
              </MenuItem>
            );
          })}
        </TextField>
        <Chip label={REVIEW_LABELS[status]} color={status === 'approved' ? 'success' : status === 'open' ? 'default' : 'primary'} />
        {data?.deadline && (
          <Chip
            variant={data.deadline.state === 'on_track' ? 'outlined' : 'filled'}
            color={DEADLINE_CHIP[data.deadline.state]}
            label={data.deadline.state === 'approved' ? 'Approved before the deadline' : `${data.deadline.state === 'overdue' ? 'Overdue, was due' : 'Due'} ${fmtDue(data.deadline)}`}
          />
        )}
        {data && (
          <Typography variant="body2" color="text.secondary">
            {data.submissions.length} of {data.instances.length} instances reported · {data.total_events} failed logins
            {data.open_actions ? ` · ${data.open_actions} open actions` : ''}
            {review.reviewer_email ? ` · reviewer ${review.reviewer_email}` : ''}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" onClick={() => exportWeek('csv')}>Export CSV</Button>
        <Button size="small" variant="outlined" onClick={() => exportWeek('pdf')}>Export PDF</Button>
      </Paper>

      {data && (
        <>
          {status === 'approved' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Approved by {review.approved_by} on {fmt(review.approved_at)}.{review.approval_comment ? ` “${review.approval_comment}”` : ''}
              <Box sx={{ fontFamily: 'monospace', fontSize: 12, mt: 0.5 }}>Evidence hash: {review.evidence_hash}</Box>
              {data.later_submissions > 0 && <Box sx={{ mt: 0.5 }}>{data.later_submissions} later report(s) were received after approval and are not part of it.</Box>}
            </Alert>
          )}
          {data.reporting.missing.length > 0 && status !== 'approved' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Waiting for {data.reporting.missing.length} of {data.reporting.missing.length + data.reporting.reported.length} instances to report: {data.reporting.missing.join(', ')}.
              The reviewers are emailed as soon as the last one reports.
            </Alert>
          )}
          {data.reporting.missing.length === 0 && status === 'open' && (
            <Alert severity="success" sx={{ mb: 2 }}>All {data.reporting.reported.length} instances have reported. The review can start.</Alert>
          )}

          {canReview && status !== 'approved' && (
            <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ mr: 1 }}>Review</Typography>
              {status === 'open' && <Button variant="contained" onClick={() => run(() => failedLoginsAPI.startReview(week), 'Review started')}>Start review</Button>}
              {status === 'in_review' && (
                <Button variant="contained" disabled={uncovered.length > 0} onClick={() => run(() => failedLoginsAPI.completeReview(week), 'Review marked complete')}>
                  Mark review complete
                </Button>
              )}
              {status === 'review_complete' && (
                <>
                  <Button variant="contained" color="success" disabled={data.open_actions > 0} onClick={() => { setText(''); setDialog({ type: 'approve' }); }}>Approve week</Button>
                  <Button variant="outlined" color="warning" onClick={() => { setText(''); setDialog({ type: 'return' }); }}>Return to review</Button>
                </>
              )}
              <Typography variant="body2" color="text.secondary">
                {status === 'open' && 'Start the review to assign actions.'}
                {status === 'in_review' && (uncovered.length ? `${uncovered.length} item(s) still need an action.` : 'Everything has an action. You can mark the review complete.')}
                {status === 'review_complete' && (data.open_actions ? 'Waiting for open actions to be completed before approval.' : 'All actions are done. Ready for approval.')}
              </Typography>
            </Paper>
          )}

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Instances ({data.submissions.length})</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell>Instance</TableCell><TableCell align="right">Failed logins</TableCell><TableCell align="right">Different logins</TableCell>
                    <TableCell align="right">Client addresses</TableCell><TableCell>Period covered</TableCell><TableCell>Received</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {data.submissions.map((s) => (
                    <TableRow key={s.submission_id}>
                      <TableCell>{s.instance_name}</TableCell>
                      <TableCell align="right">{s.event_count}</TableCell>
                      <TableCell align="right">{s.logins}</TableCell>
                      <TableCell align="right">{s.clients}</TableCell>
                      <TableCell>{fmtLocal(s.window_start)} to {fmtLocal(s.window_end)}</TableCell>
                      <TableCell>{fmt(s.received_at)}</TableCell>
                    </TableRow>
                  ))}
                  {data.reporting.missing.map((n) => (
                    <TableRow key={n}><TableCell>{n}</TableCell><TableCell colSpan={5}><Chip size="small" color="error" label="No report" /></TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Failed logins by instance and login ({rows.length})</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <TextField select size="small" label="Instance" sx={{ minWidth: 220 }} value={filters.instance_id}
                onChange={(e) => setFilters({ ...filters, instance_id: e.target.value })} slotProps={NATIVE}>
                <option value="">All instances</option>
                {data.instances.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </TextField>
              <TextField size="small" label="Search login, address or reason" sx={{ minWidth: 280 }} value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Instance</TableCell><TableCell>Login</TableCell><TableCell align="right">Failures</TableCell><TableCell>Why</TableCell>
                    <TableCell>From</TableCell><TableCell>Databases</TableCell><TableCell>First / last</TableCell><TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={exceptionKey(r)} hover>
                      <TableCell>{r.instance_name}</TableCell>
                      {r.kind === 'missing' ? (
                        <TableCell colSpan={6}><Chip size="small" color="error" label="No report received" /></TableCell>
                      ) : (
                        <>
                          <TableCell>{r.login_name || '(not recognised)'}</TableCell>
                          <TableCell align="right">{r.events}</TableCell>
                          <TableCell>{(r.categories || []).map((c) => <Chip key={c} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} label={data.categories[c] || c} />)}</TableCell>
                          <TableCell>{(r.client_list || []).join(', ') || '-'}</TableCell>
                          <TableCell>{(r.database_list || []).join(', ') || '-'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtLocal(r.first_seen)}<br />{fmtLocal(r.last_seen)}</TableCell>
                        </>
                      )}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {r.kind === 'login' && <Button size="small" onClick={() => showEvents(r)}>Events</Button>}
                        {uncoveredKeys.has(exceptionKey(r))
                          ? (editable ? <Button size="small" variant="outlined" onClick={() => openActionDialog(r)}>Assign action</Button> : <Chip size="small" color="warning" label="No action" />)
                          : <Chip size="small" color="success" label="Action assigned" />}
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && <TableRow><TableCell colSpan={8} align="center">No failed logins this week</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {data.actions.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>Actions ({data.actions.length})</Typography>
              {myOpenActions.length > 0 && <Alert severity="info" sx={{ mb: 1 }}>You have {myOpenActions.length} action(s) assigned to you to complete.</Alert>}
              {data.actions.map((a) => {
                const mine = a.assigned_to.toLowerCase() === me;
                return (
                  <Box key={a.id} sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1.5, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip size="small" label={a.status === 'done' ? 'Done' : 'Open'} color={a.status === 'done' ? 'success' : 'warning'} />
                      <Chip size="small" variant="outlined" label={a.kind === 'accepted_risk' ? 'Accepted risk' : 'Fix'} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.instance_name} · {a.login_name || 'all logins'}</Typography>
                      <Typography variant="caption" color="text.secondary">assigned to {a.assigned_to}{a.due_date ? ` · due ${a.due_date}` : ''}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{a.description}</Typography>
                    {(a.history || []).map((h) => (
                      <Typography key={h.id} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {h.event === 'assigned' ? `Assigned to ${h.new_assignee} by ${h.changed_by}`
                          : `Reassigned from ${h.old_assignee} to ${h.new_assignee} by ${h.changed_by}${h.reason ? `: ${h.reason}` : ''}`}
                        {' · '}{fmt(h.changed_at)}
                      </Typography>
                    ))}
                    {a.comments.map((c) => (
                      <Box key={c.id} sx={{ mt: 0.5, pl: 1.5, borderLeft: '3px solid #ccc' }}>
                        <Typography variant="caption" color="text.secondary">{c.author} · {fmt(c.created_at)}</Typography>
                        <Typography variant="body2">{c.comment}</Typography>
                      </Box>
                    ))}
                    {a.status === 'done' && <Typography variant="caption" color="success.main">Completed by {a.completed_by} on {fmt(a.completed_at)}</Typography>}
                    {a.status === 'open' && (mine || canReview) && status !== 'approved' && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <Button size="small" onClick={() => { setText(''); setDialog({ type: 'comment', action: a }); }}>Add comment</Button>
                        {canReview && <Button size="small" onClick={() => { setText(''); setActionForm({ ...actionForm, assigned_to: '' }); setDialog({ type: 'reassign', action: a }); }}>Reassign</Button>}
                        {mine && <Button size="small" variant="contained" color="success" onClick={() => { setText(''); setDialog({ type: 'complete', action: a }); }}>Mark complete</Button>}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Paper>
          )}
        </>
      )}

      {/* Events for one login */}
      <Dialog open={!!events} onClose={() => setEvents(null)} maxWidth="lg" fullWidth>
        <DialogTitle>{events?.group.instance_name} · {events?.group.login_name || '(not recognised)'}</DialogTitle>
        <DialogContent>
          {!events?.rows ? <CircularProgress size={24} /> : (
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow><TableCell>When (instance time)</TableCell><TableCell>Why</TableCell><TableCell>Reason</TableCell><TableCell>Database</TableCell><TableCell>From</TableCell></TableRow></TableHead>
                <TableBody>
                  {events.rows.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtLocal(e.logged_at)}</TableCell>
                      <TableCell>{data?.categories[e.category] || e.category}</TableCell>
                      <TableCell>{e.reason || '-'}</TableCell>
                      <TableCell>{e.database_name || '-'}</TableCell>
                      <TableCell>{e.client_address || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {events.rows.length === 0 && <TableRow><TableCell colSpan={5} align="center">No events</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setEvents(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Dialogs */}
      <Dialog open={dialog?.type === 'action'} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign action</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Alert severity="info">
            Only the person you assign can complete this action, and the week cannot be approved until they do.
            Every assignment and reassignment is recorded in the audit trail.
          </Alert>
          {dialog?.row && (
            <Typography variant="body2">
              {dialog.row.instance_name} · {dialog.row.kind === 'missing' ? 'no report received' : (actionForm.whole_instance ? 'every login on this instance' : dialog.row.login_name || '(not recognised)')}
            </Typography>
          )}
          {dialog?.row?.kind === 'login' && (
            <FormControlLabel
              control={<Switch checked={actionForm.whole_instance} onChange={(e) => setActionForm({ ...actionForm, whole_instance: e.target.checked })} />}
              label="Covers every failed login on this instance, not just this login"
            />
          )}
          <TextField select label="Type" value={actionForm.kind} onChange={(e) => setActionForm({ ...actionForm, kind: e.target.value })}>
            <MenuItem value="fix">Fix the problem</MenuItem>
            <MenuItem value="accepted_risk">Accept the risk (record why)</MenuItem>
          </TextField>
          <TextField select label="Assign to" value={actionForm.assigned_to} onChange={(e) => setActionForm({ ...actionForm, assigned_to: e.target.value })}>
            {users.filter((u) => u.is_active && !isReviewer(u.email)).map((u) => <MenuItem key={u.id} value={u.email}>{u.name ? `${u.name} (${u.email})` : u.email}</MenuItem>)}
          </TextField>
          {review.reviewer_email && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              {review.reviewer_email} is reviewing this week, so actions cannot be assigned to them.
            </Typography>
          )}
          <TextField label="What needs doing" multiline minRows={3} value={actionForm.description} onChange={(e) => setActionForm({ ...actionForm, description: e.target.value })} />
          <TextField label="Due date (optional)" type="date" value={actionForm.due_date}
            onChange={(e) => setActionForm({ ...actionForm, due_date: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" disabled={!actionForm.assigned_to || actionForm.description.trim().length < 5}
            onClick={() => run(() => failedLoginsAPI.createAction(week, {
              instance_id: dialog.row.instance_id,
              login_name: dialog.row.kind === 'login' && !actionForm.whole_instance ? dialog.row.login_name : null,
              kind: actionForm.kind, description: actionForm.description, assigned_to: actionForm.assigned_to, due_date: actionForm.due_date || null,
            }), (res) => (res.data.notified ? 'Action assigned and the assignee has been emailed' : 'Action assigned, but the email could not be sent. Please tell them directly.'))}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog?.type === 'comment'} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Add comment</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}><TextField fullWidth multiline minRows={3} label="Comment" value={text} onChange={(e) => setText(e.target.value)} /></DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" disabled={!text.trim()} onClick={() => run(() => failedLoginsAPI.addComment(dialog.action.id, text), 'Comment added')}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog?.type === 'complete'} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark action complete</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Typography variant="body2" sx={{ mb: 2 }}>Say what was done. Once complete, the action is locked.</Typography>
          <TextField fullWidth multiline minRows={3} label="What was done" value={text} onChange={(e) => setText(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" disabled={text.trim().length < 5} onClick={() => run(() => failedLoginsAPI.completeAction(dialog.action.id, text), 'Action completed')}>Complete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog?.type === 'reassign'} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reassign action</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Alert severity="info">Currently assigned to {dialog?.action?.assigned_to}. Both people are emailed and the change is recorded with your reason.</Alert>
          <TextField select label="Assign to" value={actionForm.assigned_to} onChange={(e) => setActionForm({ ...actionForm, assigned_to: e.target.value })}>
            {users.filter((u) => u.is_active && !isReviewer(u.email) && u.email.toLowerCase() !== (dialog?.action?.assigned_to || '').toLowerCase())
              .map((u) => <MenuItem key={u.id} value={u.email}>{u.name ? `${u.name} (${u.email})` : u.email}</MenuItem>)}
          </TextField>
          <TextField label="Why" multiline minRows={2} value={text} onChange={(e) => setText(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" disabled={!actionForm.assigned_to || text.trim().length < 5}
            onClick={() => run(() => failedLoginsAPI.reassignAction(dialog.action.id, actionForm.assigned_to, text), 'Action reassigned')}>Reassign</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog?.type === 'approve'} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve week of {week}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>Approving locks this week and fixes the evidence. It cannot be undone.</Alert>
          <TextField fullWidth multiline minRows={2} label="Comment (optional)" value={text} onChange={(e) => setText(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={() => run(() => failedLoginsAPI.approve(week, text), 'Week approved')}>Approve</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog?.type === 'return'} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Return to review</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}><TextField fullWidth multiline minRows={2} label="Why" value={text} onChange={(e) => setText(e.target.value)} /></DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={text.trim().length < 5} onClick={() => run(() => failedLoginsAPI.returnWeek(week, text), 'Returned to review')}>Return</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FailedLogins;
